import {SxProps, TabsProps, Theme} from '@mui/material';
import theme from 'src/theme';

/**
 * The "relationships" panel, shared by the driver, company and employee pages.
 *
 * All three carried their own copy of this shell: an 8px panel on `text.secondary` — a token that
 * means "text on a dark ground" and happens to be white — with its own padding, a heading floating
 * inside it and a tab strip on a hairline. It is the shared card now, with the same section header
 * and the same tab band the tariff description uses, so the tables inside sit flush against the
 * card's edges like the ones on Chargers and Locations.
 */
export const tabsRootSx: SxProps<Theme> = {
    width: '100%',
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
};

export const panelHeaderSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    px: 3,
    py: 2,
};

export const panelBadgeSx: SxProps<Theme> = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '18px'},
};

export const panelTitleSx: SxProps<Theme> = {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
};

/** The tabs as a band, like the filter band on a list. */
export const tabsBandSx: SxProps<Theme> = {
    px: 3,
    background: theme.palette.neutral[50],
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    borderBottom: `1px solid ${theme.palette.neutral[100]}`,
    minHeight: 0,
    '.MuiTabs-indicator': {
        height: '2px',
        background: theme.palette.primary.main,
    },
};

/**
 * The band's tabs scroll sideways instead of being clipped.
 *
 * MUI's default `standard` variant gives the scroller `overflow-x: hidden`, so a strip wider than the
 * screen is simply cut off - there is no gesture that reaches the rest. On a phone that put the last
 * three tabs of the company panel (Employees, Invoice Items, Credentials) out of reach entirely,
 * which is a tab nobody can open rather than a tab that looks wrong.
 *
 * `scrollButtons: 'auto'` renders the arrows only when there is something to scroll, so a desktop
 * where every tab fits draws exactly what it drew before; `allowScrollButtonsMobile` keeps them on
 * the narrow screens that are the entire point, since MUI hides them below `sm` by default.
 *
 * Props rather than sx, because this is a prop decision in MUI - and kept here beside the band's
 * styling so that all four panels sharing this shell behave the same, instead of each call site
 * having to remember three attributes.
 */
export const tabsBandProps: Pick<TabsProps, 'variant' | 'scrollButtons' | 'allowScrollButtonsMobile'> = {
    variant: 'scrollable',
    scrollButtons: 'auto',
    allowScrollButtonsMobile: true,
};

export const tabSx: SxProps<Theme> = {
    color: theme.palette.neutral[600],
    fontSize: '0.75rem',
    fontWeight: 500,
    textTransform: 'capitalize',
    px: 0,
    py: 1.5,
    minHeight: 0,
    minWidth: 0,
    '&.Mui-selected': {
        color: theme.palette.primary.main,
        fontWeight: 600,
    },
    '&:not(:last-of-type)': {
        mr: 3,
    },
};

/**
 * The tab as a heading.
 *
 * On the business page the tabs are the only thing naming the table — there is no section title
 * above them — so they are sized like one. In the relationships panels they sit under a heading of
 * their own and stay small, which is why this is a second style rather than a change to the first.
 */
export const tabLargeSx: SxProps<Theme> = {
    color: theme.palette.neutral[600],
    fontSize: '0.875rem',
    fontWeight: 500,
    textTransform: 'none',
    px: 0,
    py: 1.6,
    minHeight: 0,
    minWidth: 0,
    '&.Mui-selected': {
        color: theme.palette.primary.main,
        fontWeight: 600,
    },
    '&:not(:last-of-type)': {
        mr: 3.5,
    },
};

/** No top margin: the tab band already separates the content from the tabs. */
export const tabSelectedSx: SxProps<Theme> = {
    mt: 0,
};
